import { useEffect, useRef, useState } from 'react';

/**
 * Counts a number up to its value instead of snapping to it.
 *
 * A figure that animates into place gets read. One that is simply there when
 * the page arrives is furniture — the eye has moved on by the time it settles.
 *
 * It counts from wherever it currently is, not from zero, so a figure that moves
 * because a sale was just rung up slides from the old number to the new one
 * rather than falling back to nothing and climbing again.
 *
 * Anyone who has asked their system for less movement gets the number
 * immediately, with none.
 */
const EASE_OUT = (t) => 1 - Math.pow(1 - t, 3);

export const useCountUp = (target, duration = 650) => {
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);
  const frameRef = useRef(0);

  useEffect(() => {
    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || !Number.isFinite(target)) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }

    const from = Number.isFinite(fromRef.current) ? fromRef.current : 0;
    if (from === target) return undefined;

    const started = performance.now();

    const step = (now) => {
      const progress = Math.min((now - started) / duration, 1);
      setShown(from + (target - from) * EASE_OUT(progress));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
        setShown(target);
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);

  return shown;
};
